import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Switch,
  Text,
  VStack,
  useClipboard,
  HStack,
} from '@chakra-ui/react';
import { ButtonCustom, CheckboxCustom, InputCustom } from '../../components/UI';
import { useAppDispatch, useAppSelector } from '../../../utils/reduxHooks';
import { updatePasswordAction } from '../../../modules/user/actions';
import {
  selectUserEmail,
  selectUserPhone,
  selectUserPk,
  selectUserFirstName,
  selectUserLastName,
} from '../../../modules/user/selectors';
import useCustomToast from '../../components/UI/toast';

const PASSWORD_LENGTH = 6;

export const AccountSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const toast = useCustomToast();
  const firstName = useAppSelector(selectUserFirstName);
  const lastName = useAppSelector(selectUserLastName);
  const email = useAppSelector(selectUserEmail);
  const phone = useAppSelector(selectUserPhone);
  const userId = useAppSelector(selectUserPk);

  const { hasCopied, onCopy } = useClipboard(userId);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handlePasswordChange = () => {
    if (newPassword !== confirmPassword) {
      setErrorMessage('Пароли не совпадают');
      return;
    }
    if (newPassword.length < PASSWORD_LENGTH) {
      setErrorMessage(`Пароль должен быть не менее ${PASSWORD_LENGTH} символов`);
      return;
    }

    dispatch(
      updatePasswordAction.request({
        old_password: currentPassword,
        password: newPassword,
        onSuccess: () => {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setErrorMessage(null);
          toast('Пароль успешно изменен', 'success');
        },
        onFailure: (error: string) => {
          toast(error || 'Ошибка при изменении пароля', 'error');
        },
      }),
    );
  };

  return (
    <Box maxW="1100px" mx="auto" p={8}>
      <Heading size="lg" mb={8}>
        Настройки аккаунта
      </Heading>

      <Flex gap={8}>
        {/* Левый блок */}
        <VStack flex="1" spacing={8} align="stretch">
          {/* Контактная информация */}
          <Box bg="#F4F4F4" p={6} borderRadius="lg" boxShadow="md">
            <Heading size="md" mb={4}>
              Контактная информация
            </Heading>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Имя</FormLabel>
                <InputCustom
                  isReadOnly
                  placeholder="Имя"
                  defaultValue={firstName || 'Не указано'}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Фамилия</FormLabel>
                <InputCustom
                  isReadOnly
                  placeholder="Фамилия"
                  defaultValue={lastName || 'Не указано'}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Название компании</FormLabel>
                <InputCustom
                  isReadOnly
                  placeholder="Название компании"
                  defaultValue={'Не указано'}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Почта</FormLabel>
                <InputCustom
                  isReadOnly
                  type="email"
                  placeholder="Почта"
                  defaultValue={email || 'Не указано'}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Телефон *</FormLabel>
                <InputCustom
                  isReadOnly
                  placeholder="Телефон"
                  defaultValue={phone || 'Не указано'}
                />
              </FormControl>
            </VStack>
          </Box>

          {/* Смена пароля */}
          <Box bg="#F4F4F4" p={6} borderRadius="lg" boxShadow="md">
            <Heading size="md" mb={4}>
              Смена пароля
            </Heading>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Текущий пароль</FormLabel>
                <InputCustom
                  type="password"
                  placeholder="*********"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Новый пароль</FormLabel>
                <InputCustom
                  type="password"
                  placeholder="*********"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Повторите пароль</FormLabel>
                <InputCustom
                  type="password"
                  placeholder="*********"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
              </FormControl>
              {errorMessage && <Text color="red.500">{errorMessage}</Text>}
              <Button
                colorScheme="blackAlpha"
                color="white"
                bg="black"
                mt={4}
                w="100%"
                onClick={handlePasswordChange}
              >
                Сохранить
              </Button>
            </VStack>
          </Box>
        </VStack>

        {/* Правый блок */}
        <VStack flex="1" spacing={8} align="stretch">
          {/* API-ключ */}
          <Box bg="#F4F4F4" p={6} borderRadius="lg" boxShadow="md">
            <Heading size="md" mb={4}>
              API-ключ
            </Heading>
            <Flex align="center">
              <InputCustom value={userId} isReadOnly bg="white" mr={2} />
              <ButtonCustom variant="withBackground" onClick={onCopy} colorScheme="blackAlpha">
                {hasCopied ? 'Скопировано' : 'Скопировать'}
              </ButtonCustom>
            </Flex>
          </Box>

          {/* Управление рассылками и уведомлениями */}
          <Box bg="#F4F4F4" p={6} borderRadius="lg" boxShadow="md">
            <Heading size="md" mb={4}>
              Управление рассылками и уведомлениями
            </Heading>

            {/* Уведомления об акциях */}
            <FormControl display="flex" alignItems="center" mb={4}>
              <Switch id="promo" colorScheme="teal" mr={2} defaultChecked />
              <FormLabel htmlFor="promo" mb="0" fontWeight="normal">
                Включить рассылки об акциях и предложениях
              </FormLabel>
            </FormControl>
            <VStack spacing={2} align="stretch">
              <CheckboxControl label="СМС" />
              <CheckboxControl label="Email" />
              <CheckboxControl label="Telegram" />
            </VStack>

            <Divider my={4} />

            {/* Уведомления по заказам */}
            <FormControl display="flex" alignItems="center" mb={4}>
              <Switch id="order" colorScheme="teal" mr={2} defaultChecked />
              <FormLabel htmlFor="order" mb="0" fontWeight="normal">
                Включить уведомления по заказам
              </FormLabel>
            </FormControl>
            <VStack spacing={2} align="stretch">
              <CheckboxControl label="Email" />
              <CheckboxControl label="Telegram" />
              <CheckboxControl label="СМС" />
            </VStack>
          </Box>
        </VStack>
      </Flex>
    </Box>
  );
};

const CheckboxControl = ({ label }: { label: string }) => (
  <HStack>
    <Flex
      width="100%"
      justifyContent="space-between"
      alignItems="center"
      h="52px"
      borderRadius={8}
      backgroundColor="white"
      p={3}
    >
      <Text>{label}</Text>
      <CheckboxCustom />
    </Flex>
  </HStack>
);
